import http from '@utils/http';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { firebaseSignOut, getIdTokenOrNull, signInWithPortal, syncSessionFromFirebaseUser } from '@services/registry';
import { rememberAuthPortal } from '@utils/routing';

/**
 * Auth state is driven by Firebase Auth + Firestore (`owners` / `tenant_users`).
 * Optional HTTP helpers remain for legacy profile endpoints if you still run an API.
 */
export const useAuthStore = create(
    persist(
        (set, get) => ({
            user: null,
            role: null,
            permissions: [],
            isAuthenticated: false,
            accessToken: null,
            refreshToken: null,
            isRefreshing: true,

            /** Called from Firebase auth listener after token/profile sync */
            applyFirebaseSession: async (firebaseUser) => {
                if (!firebaseUser) {
                    set({
                        user: null,
                        role: null,
                        permissions: [],
                        isAuthenticated: false,
                        accessToken: null,
                        refreshToken: null,
                    });
                    return;
                }
                const session = await syncSessionFromFirebaseUser(firebaseUser);
                if (!session) {
                    await firebaseSignOut();
                    set({
                        user: null,
                        role: null,
                        permissions: [],
                        isAuthenticated: false,
                        accessToken: null,
                        refreshToken: null,
                    });
                    return;
                }
                const token = await getIdTokenOrNull(session.firebaseUser);
                set({
                    user: session.user,
                    role: session.user.role,
                    permissions: session.user.permissions || [],
                    isAuthenticated: true,
                    accessToken: token,
                    refreshToken: null,
                });
            },

            onboardTheClient: async (data) => {
                const currentUser = get().user;
                try {
                    const response = await http.patch('/user/onboard-the-client', data);
                    if (response?.data?.success) {
                        set({
                            user: {
                                ...currentUser,
                                isOnboarded: true,
                            },
                        });
                    }
                    return { message: response?.data?.data?.message || response?.data?.message };
                } catch (error) {
                    const errorMessage =
                        error?.response?.data?.error?.message ||
                        error?.response?.data?.message ||
                        error?.response?.data?.error ||
                        error?.message ||
                        'Onboard client failed';
                    throw new Error(errorMessage);
                }
            },

            updateUserStore: async (userData) => {
                const currentUser = get().user;
                const allowedFields = ['name', 'email', 'phone'];
                const updatedUserData = {};

                for (const field of allowedFields) {
                    const newValue = userData[field];
                    const currentValue = currentUser[field];

                    const isFieldEmpty = newValue === undefined || newValue === null || newValue === '';
                    const isNewButEmpty = currentValue === undefined && isFieldEmpty;
                    const isChanged = newValue && newValue !== currentValue;

                    if (!isNewButEmpty && isChanged) {
                        updatedUserData[field] = newValue;
                    }
                }

                if (Object.keys(updatedUserData).length === 0) {
                    return { status: 304, message: 'No changes detected' };
                }

                try {
                    const response = await http.patch('/user/update/profile', updatedUserData);
                    set((state) => ({
                        user: {
                            ...state.user,
                            ...updatedUserData,
                        },
                    }));
                    return { message: response?.data?.data?.message || response?.data?.message };
                } catch (error) {
                    const errorMessage =
                        error?.response?.data?.error?.message ||
                        error?.response?.data?.message ||
                        error?.response?.data?.error ||
                        error?.message ||
                        'Update user failed';
                    throw new Error(errorMessage);
                }
            },

            patchUser: (partial) => {
                if (!partial || typeof partial !== 'object') return;
                set((state) => ({
                    user: state.user ? { ...state.user, ...partial } : state.user,
                }));
            },

    /**
     * @param {'owner'|'tenant'} portal — must match `/owner/sign-in` vs `/tenant/sign-in`
     */
            login: async (email, password, portal) => {
                set({ isRefreshing: true });
                try {
                    const { user, firebaseUser } = await signInWithPortal(email, password, portal);
                    rememberAuthPortal(portal);
                    const token = await getIdTokenOrNull(firebaseUser);
                    set({
                        user,
                        role: user.role,
                        permissions: user.permissions || [],
                        isAuthenticated: true,
                        accessToken: token,
                        refreshToken: null,
                        isRefreshing: false,
                    });
                    return { user };
                } catch (error) {
                    set({ isRefreshing: false });
                    const errorMessage = error?.message || 'Login failed';
                    throw new Error(errorMessage);
                }
            },

            register: async (formData) => {
                try {
                    const response = await http.post('/auth/register', formData);
                    const { data } = response.data;

                    if (response.status === 200 && response.data.message !== '') {
                        return {
                            routeToLogin: true,
                            message: response.data.message,
                        };
                    }
                    set({
                        user: data.user,
                        role: data.user.role,
                        permissions: data.user.permissions || [],
                        isAuthenticated: true,
                        accessToken: data.accessToken,
                        refreshToken: data.refreshToken,
                        isRefreshing: false,
                    });
                    return response;
                } catch (error) {
                    const errorMessage =
                        error?.response?.data?.error?.message ||
                        error?.response?.data?.message ||
                        error?.response?.data?.error ||
                        error?.message ||
                        'Registration failed';
                    throw new Error(errorMessage);
                }
            },

            logout: async () => {
                try {
                    await firebaseSignOut();
                } catch (e) {
                    console.error(e);
                }
                set({
                    user: null,
                    role: null,
                    permissions: [],
                    isAuthenticated: false,
                    accessToken: null,
                    refreshToken: null,
                    isRefreshing: false,
                });
            },

            hardLogout: () => {
                set({
                    user: null,
                    role: null,
                    permissions: [],
                    isAuthenticated: false,
                    accessToken: null,
                    refreshToken: null,
                    isRefreshing: false,
                });
            },

            checkAuth: async () => {
                // Firebase listener handles session; keep for API compatibility
                return { success: get().isAuthenticated };
            },
        }),
        {
            name: 'school-crm-auth',
            partialize: (state) => ({
                user: state.user,
                role: state.role,
                permissions: state.permissions,
                isAuthenticated: state.isAuthenticated,
                accessToken: state.accessToken,
            }),
        }
    )
);
